import { Anchor, Box, Button, Rhythm, Stack, Text } from '@trmediaab/zebra';
import { Spinner } from '@trmediaab/zebra-icons';
import type { CheckoutUser } from '@trmediaab/zebra-types/trhub';
import { useCallback, useEffect, useReducer } from 'react';

import Card from '../../../components/Card';
import ErrorMessage from '../../../components/ErrorMessage';
import { Input, SelectInput } from '../../../components/Form';
import TextButton from '../../../components/TextButton';
import type { ErrorWithMessage } from '../../../types/ErrorWithMessage';
import { ensureErrorWithMessage } from '../../../utils/ensureErrorWithMessage';
import { extraInfoRequest as request } from '../../../utils/request';
import InfoText from '../InfoText';
import type { PaymentFormProps } from '../types';
import type { Bank } from './useGetBankAccounts';
import useGetBankAccounts, {
  paymentServiceSuppliers,
} from './useGetBankAccounts';

type Action =
  | {
      type: 'formChange';
      payload: {
        name: string;
        value: string;
      };
    }
  | {
      type: 'setBankIdSameDevice';
      payload: boolean;
    }
  | {
      type: 'caughtClientError';
      payload: ErrorWithMessage;
    }
  | {
      type: 'clearClientError';
      payload?: never;
    };

interface State {
  ssn: string;
  bank: Bank | '';
  accountNumber: string;
  clientError: ErrorWithMessage | null;
  bankIdSameDevice: boolean;
}

const bankIdOptions = [
  {
    label: 'BankID på den här enheten',
    sameDevice: true,
  },
  {
    label: 'BankID på en annan enhet',
    sameDevice: false,
  },
] as const;

function initState(user?: CheckoutUser): State {
  return {
    ssn: user?.personalIdentityNumber || '',
    bank: '',
    accountNumber: '',
    clientError: null,
    bankIdSameDevice: /iphone|android/i.test(navigator?.userAgent || ''),
  };
}

function reducer(state: State, action: Action) {
  const { type, payload } = action;
  switch (type) {
    case 'formChange': {
      const { name, value } = payload;
      return {
        ...state,
        [name]: value,
      };
    }
    case 'setBankIdSameDevice':
      return {
        ...state,
        bankIdSameDevice: payload,
      };

    case 'caughtClientError':
      return {
        ...state,
        clientError: payload,
      };

    case 'clearClientError':
      return {
        ...state,
        clientError: null,
      };

    default:
      return state;
  }
}

const AutogiroForm = ({
  order,
  site,
  user,
  onComplete,
  onAuthentication,
}: PaymentFormProps) => {
  const [state, dispatch] = useReducer(reducer, user, initState);

  const [action, bankAccountState, reset] = useGetBankAccounts({
    orderId: order.id,
  });

  const status = bankAccountState.status;
  const accountNumbers =
    'accountNumbers' in bankAccountState
      ? bankAccountState.accountNumbers
      : undefined;
  const qr = 'qr' in bankAccountState ? bankAccountState.qr : undefined;
  const autoStartToken =
    'autoStartToken' in bankAccountState
      ? bankAccountState.autoStartToken
      : undefined;
  const autoStartTokenIsRequired =
    'autoStartTokenIsRequired' in bankAccountState
      ? bankAccountState.autoStartTokenIsRequired
      : undefined;

  const remoteError =
    'error' in bankAccountState ? bankAccountState.error : undefined;

  const openBankId = useCallback(() => {
    if (status === 'Waiting' && (autoStartToken || !autoStartTokenIsRequired)) {
      const parts = [];

      if (autoStartToken) {
        parts.push(`autostarttoken=${autoStartToken}`);
      }
      // Redirect must bet set last of some reason
      parts.push(`redirect=null`);

      // Redirect the user to bankId, redirect=null will send the user
      // back to the same window after authentication
      window.location.href = `bankid:///?${parts.join('&')}`;
    }
  }, [autoStartToken, autoStartTokenIsRequired, status]);

  // Open automatically
  useEffect(() => {
    if (state.bankIdSameDevice) {
      openBankId();
    }
  }, [state.bankIdSameDevice, openBankId]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      dispatch({ type: 'formChange', payload: { name, value } });
    },
    [],
  );

  // Start bankId service and poll for bank accounts
  const fetchAccounts = useCallback(async () => {
    dispatch({ type: 'clearClientError' });
    try {
      if (!state.bank || state.bank.length === 0) {
        throw new Error('Bank måste anges');
      }

      if (!/^\d{8}-?\d{4}$/.test(state.ssn)) {
        throw new Error(
          'Personnummer måste anges med 12 siffror YYYYMMDD-XXXX',
        );
      }

      await action({
        ssn: state.ssn,
        bank: state.bank,
      });
    } catch (error) {
      dispatch({
        type: 'caughtClientError',
        payload: ensureErrorWithMessage(error),
      });
    }
  }, [action, state.bank, state.ssn]);

  // Get accountInfo for the selected account
  const accountInfo = accountNumbers?.find(
    item => item.Number === state.accountNumber,
  );

  const onSubmit = useCallback(async () => {
    try {
      const { res } = await request('autogiro/create-approval', {
        method: 'POST',
        body: {
          ...state,
          orderId: order.id,
          accountInfo,
          site,
        },
      });

      // New users gets a temporary login session when the order is completed
      if (res.headers.get('x-temporary-authentication')) {
        onAuthentication?.();
      }
      onComplete();
    } catch (error) {
      dispatch({
        type: 'caughtClientError',
        payload: ensureErrorWithMessage(error),
      });
    }
  }, [accountInfo, onAuthentication, onComplete, order.id, state, site]);

  const accountOptions =
    accountNumbers
      ?.filter(item => item.AutogiroCompatible === 'Approved')
      ?.map(item => ({
        label: `${item.Number} - ${item.Name}`,
        value: item.Number,
      })) || [];

  const error = state.clientError || remoteError;
  const disableChanges = status !== 'Initial' && status !== 'Failed';

  return (
    <Rhythm between="5">
      <InfoText
        user={user}
        text="Fyll i dina uppgifter nedan och signera med BankID för att aktivera månadsbetalning med autogiro."
      />
      <Card variant="secondary">
        <Rhythm between="4">
          <Input
            label="Personnummer"
            name="ssn"
            placeholder="ÅÅÅÅMMDD-NNNN"
            minLength={12}
            maxLength={13}
            value={state.ssn}
            onChange={onChange}
            disabled={disableChanges}
          />

          <SelectInput
            label="Bank"
            name="bank"
            width="100%"
            value={state.bank}
            disabled={disableChanges}
            onChange={onChange}
            data={[
              { label: 'Välj bank', value: '' },
              ...Object.entries(paymentServiceSuppliers).map(
                ([value, label]) => ({
                  value,
                  label,
                }),
              ),
            ]}
          />

          {error && <ErrorMessage error={error} />}

          {disableChanges && (
            <TextButton
              label="Börja om"
              onClick={() => {
                dispatch({ type: 'clearClientError' });
                reset();
              }}
            />
          )}

          {status === 'Failed' && (
            <Button
              type="button"
              onClick={fetchAccounts}
              bc="checkoutPrimary"
              width="100%"
            >
              Försök igen
            </Button>
          )}

          {status === 'Initial' && (
            <Stack axis="horizontal" between="2,5" pt="2">
              {bankIdOptions.map(item => (
                <Button
                  key={item.sameDevice.toString()}
                  disabled={!state.ssn || !state.bank}
                  type="button"
                  onClick={() => {
                    dispatch({
                      type: 'setBankIdSameDevice',
                      payload: item.sameDevice,
                    });
                    fetchAccounts();
                  }}
                  bc="checkoutPrimary"
                  width="100%"
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          )}

          {qr && !state.bankIdSameDevice && status === 'Waiting' && (
            <Rhythm between="5">
              <Text textAlign="center" maxWidth="340px" mx="auto">
                Starta BankID-appen på din telefon eller surfplatta och tryck på
                ikonen för QR-kod i appen för att skanna QR-koden.
              </Text>

              <Box
                as="img"
                alt="Qr code"
                src={qr}
                maxWidth="240px"
                mx="auto"
                sx={{
                  width: '100%',
                  height: 'auto',
                  marginBottom: '20px',
                  maxWidth: '240px',
                }}
              />
            </Rhythm>
          )}

          {/*
            Note: A QR-code is not always returned, for example Danske Bank don't.
            In that case, just tell the user to open the app.
          */}
          {!qr &&
            !state.bankIdSameDevice &&
            autoStartTokenIsRequired === false &&
            status === 'Waiting' && (
              <Text textAlign="center" maxWidth="340px" mx="auto">
                Starta BankID-appen i din telefon eller surfplatta och
                legitimera dig.
              </Text>
            )}

          {status === 'Loading' && (
            <Text textAlign="center">
              <Spinner size="buttonHeights.medium" />
            </Text>
          )}

          {state.bankIdSameDevice && status === 'Waiting' && (
            <Text textAlign="center">Väntar på BankID...</Text>
          )}

          {status === 'Success' && (
            <Rhythm between="5">
              <SelectInput
                label="Konto"
                name="accountNumber"
                width="100%"
                value={state.accountNumber}
                data={[{ label: 'Välj konto', value: '' }, ...accountOptions]}
                onChange={onChange}
              />

              <Button
                type="button"
                disabled={state.accountNumber === ''}
                onClick={onSubmit}
                bc="checkoutPrimary"
                width="100%"
              >
                Starta prenumeration
              </Button>
            </Rhythm>
          )}
        </Rhythm>
      </Card>
      <Text color="text.faded">
        Genom att gå vidare godkänner jag att en kontoinformationstjänst
        tillhandahålls i enlighet med{' '}
        <Anchor
          fontWeight="semiBold"
          href="https://app.billecta.com/ht/accountinformationservices"
          target="_blank"
          rel="noreferrer noopener"
        >
          Billecta Payments användarvillkor
        </Anchor>
        .
      </Text>
    </Rhythm>
  );
};

export default AutogiroForm;
